import { useEffect, useState, useMemo } from 'react';
import { AlertTriangle, AlertCircle, CheckCircle, Download } from 'lucide-react';
import { FilterBar } from './components/FilterBar';
import { KPICard } from './components/KPICard';
import { useFilters } from './components/FilterContext';
import { transformDashboardData } from '../../utils/dashboardDataTransform';
import axiosInstance from '../../utils/axios.js';

const IDEAL_RANGES = {
  ph: { min: 6.5, max: 7.5 },
  organic_carbon: { min: 0.8, max: 2.0 },
  nitrogen: { min: 280, max: 560 },
  phosphorus: { min: 10, max: 25 },
  potassium: { min: 110, max: 280 },
  zinc: { min: 0.5, max: 1.0 },
  iron: { min: 5.0, max: 10.0 },
  boron: { min: 0.1, max: 0.5 },
};

export function AdvisoryPriority() {
  const { filters } = useFilters();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSegment, setShowSegment] = useState('High');
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get('/admin/dashboard-data');
      const transformedData = transformDashboardData(response.data.dashboardData || []);

      const dataWithSegment = transformedData.map(item => {
        const score = item.soil_health_score || 0;
        let advisory_segment = 'Low';
        if (score < 40) {
          advisory_segment = 'High';
        } else if (score < 70) {
          advisory_segment = 'Moderate';
        }
        return { ...item, advisory_segment };
      });

      setData(dataWithSegment);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.response?.data?.msg || 'Failed to load dashboard data');
      setData([]);
    } finally {
      setLoading(false);
    }
  }

  const globalFilteredData = useMemo(() => {
    if (!data.length) return [];
    
    return data.filter(item => {
      if (filters.villages && filters.villages.length > 0) {
        if (!filters.villages.includes(item.farmers?.village)) return false;
      }
      if (filters.previousCrop && filters.previousCrop.length > 0) {
        if (!filters.previousCrop.includes(item.previous_crop)) return false;
      }
      if (filters.farmSize && filters.farmSize.length > 0) {
        const size = parseFloat(item.farm_size);
        if (isNaN(size)) return false;
        
        const matchesSize = filters.farmSize.some(filterOption => {
          if (filterOption === '< 1 acre') return size < 1;
          if (filterOption === '1-2 acres') return size >= 1 && size <= 2;
          if (filterOption === '> 2 acres') return size > 2;
          return false;
        });

        if (!matchesSize) return false;
      }
      return true;
    });
  }, [data, filters]);

  const uniqueFarmersData = useMemo(() => {
    const map = new Map();
    
    globalFilteredData.forEach(item => {
      const uniqueKey = item.farmer_id; 
      if (uniqueKey && !map.has(uniqueKey)) {
        map.set(uniqueKey, item); 
      }
    });
    
    return Array.from(map.values());
  }, [globalFilteredData]);

  const highPriority = uniqueFarmersData.filter(d => d.advisory_segment === 'High');
  const moderatePriority = uniqueFarmersData.filter(d => d.advisory_segment === 'Moderate');
  const lowPriority = uniqueFarmersData.filter(d => d.advisory_segment === 'Low');

  const advisoryTypes = {
    'Increase organic matter': uniqueFarmersData.filter(d =>d.organic_carbon !== null && d.organic_carbon < IDEAL_RANGES.organic_carbon.min).length,
    'Manage high pH': uniqueFarmersData.filter(d => d.ph !== null && d.ph > IDEAL_RANGES.ph.max).length,
    'Reduce P fertilizer': uniqueFarmersData.filter(d => d.phosphorus !== null && d.phosphorus > IDEAL_RANGES.phosphorus.max).length,
    'Correct K deficiency': uniqueFarmersData.filter(d => d.potassium !== null && d.potassium < IDEAL_RANGES.potassium.min).length,
    'Micronutrient foliar sprays': uniqueFarmersData.filter(d =>
      (d.zinc !== null && (d.zinc < IDEAL_RANGES.zinc.min)) ||
      (d.iron !== null && (d.iron < IDEAL_RANGES.iron.min)) ||
      (d.boron !== null && (d.boron < IDEAL_RANGES.boron.min))
    ).length,
  };

  const villageMap = Array.from(new Set(uniqueFarmersData.map(d => d.farmers?.village))).map(village => {
    const villageData = uniqueFarmersData.filter(d => d.farmers?.village === village);
    const high = villageData.filter(d => d.advisory_segment === 'High').length;
    const avgScore = villageData.length > 0
      ? Math.round(villageData.reduce((sum, d) => sum + (d.soil_health_score || 0), 0) / villageData.length)
      : 0;

    return { village, highPriority: high, avgScore, total: villageData.length };
  });

  const segmentFilteredData = uniqueFarmersData.filter(d => d.advisory_segment === showSegment);

  if (loading) {
    return (
      <div className="p-8">
        <FilterBar data={data} />
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading data...</div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8">
        <FilterBar data={data} />
        <div className="text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#F5F5F5]">
      <FilterBar data={data} />

      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6 sm:mb-8">
          <KPICard
            title="High Priority Farmers"
            value={highPriority.length}
            subtitle={`${uniqueFarmersData.length > 0 ? Math.round((highPriority.length / uniqueFarmersData.length) * 100) : 0}% of total`}
            icon={AlertTriangle}
            color="red"
            bgTint
          />
          <KPICard
            title="Moderate Priority Farmers"
            value={moderatePriority.length}
            subtitle={`${uniqueFarmersData.length > 0 ? Math.round((moderatePriority.length / uniqueFarmersData.length) * 100) : 0}% of total`}
            icon={AlertCircle}
            color="amber"
            bgTint
          />
          <KPICard
            title="Low Priority Farmers"
            value={lowPriority.length}
            subtitle={`${uniqueFarmersData.length > 0 ? Math.round((lowPriority.length / uniqueFarmersData.length) * 100) : 0}% of total`}
            icon={CheckCircle}
            color="green"
            bgTint
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <AdvisoryTypeChart advisoryTypes={advisoryTypes} />
          <VillageMapGrid villages={villageMap} />
        </div>

        <PriorityFarmerTable
          data={segmentFilteredData}
          showSegment={showSegment}
          onSegmentChange={setShowSegment}
        />
      </div>
    </div>
  );
}

function AdvisoryTypeChart({ advisoryTypes }) {
  const total = Object.values(advisoryTypes).reduce((a, b) => a + b, 0);

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Advisory Type Distribution
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Number of farmers requiring each advisory based on soil test results. A farmer may appear in more than one advisory category.
        </p>
      </div>
      <div className="space-y-4">
        {Object.entries(advisoryTypes).map(([type, count]) => {
          const percent = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={type}>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-700 font-medium">{type}</span>
                <span className="text-gray-600">{count} farmers</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-8">
                <div
                  className="bg-[#2E7D32] h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                  style={{ width: `${percent}%` }}
                >
                  {count > 0 && `${count}`}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VillageMapGrid({ villages }) {
  const sortedVillages = [...villages].sort((a, b) => b.highPriority - a.highPriority).slice(0, 15);

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Priority Distribution by Village
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Showing the 15 villages with the highest number of high-priority farmers. Red indicates high-priority farmers, while gray represents all other farmers.
          </p>
        </div>
        <div className="flex flex-wrap gap-3 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-[#D32F2F]"></div>
            <span className="text-gray-600">High Priority</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-gray-300"></div>
            <span className="text-gray-600">Other</span>
          </div>
        </div>
      </div>
      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
        {sortedVillages.map(village => {
          const highPercent = village.total > 0 ? (village.highPriority / village.total) * 100 : 0;

          const scoreColor = village.avgScore < 40
            ? 'text-red-600 font-semibold'
            : village.avgScore < 70
              ? 'text-amber-600 font-semibold'
              : 'text-green-600 font-semibold';

          return (
            <div key={village.village} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-700 font-medium">{village.village}</span>
                <span className={scoreColor}>Score: {village.avgScore}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-8 relative overflow-hidden">
                <div className="h-8 rounded-full flex items-center relative w-full">
                  <div
                    className="h-8 rounded-full absolute left-0 top-0 bg-[#D32F2F]"
                    style={{ width: `${highPercent}%` }}
                  />
                  <div
                    className="h-8 rounded-full absolute left-0 top-0 bg-gray-300"
                    style={{ width: `${100 - highPercent}%`, left: `${highPercent}%` }}
                  />
                </div>
              </div>
              <div className="text-xs text-gray-500">
                {village.highPriority} / {village.total} high priority
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PriorityFarmerTable({ data, showSegment, onSegmentChange }) {
  const exportCSV = () => {
    const headers = ['Farmer Name', 'Phone', 'Village', 'Soil Score', 'Key Issues', 'Primary Advisory'];
    const rows = data.map(d => {
      const issues = [];
      if (d.organic_carbon !== null && d.organic_carbon < IDEAL_RANGES.organic_carbon.min) issues.push('Low OC');
      if (d.ph !== null && d.ph > IDEAL_RANGES.ph.max) issues.push('High pH');
      if (d.phosphorus !== null && d.phosphorus > IDEAL_RANGES.phosphorus.max) issues.push('High P');
      if (d.potassium !== null && d.potassium < IDEAL_RANGES.potassium.min) issues.push('Low K');
      if ((d.zinc !== null && d.zinc < IDEAL_RANGES.zinc.min) || (d.iron !== null && d.iron < IDEAL_RANGES.iron.min) || (d.boron !== null && d.boron < IDEAL_RANGES.boron.min)) {
        issues.push('Micronutrient');
      }

      return [
        d.farmers?.name || '',
        d.farmers?.phone_number || '',
        d.farmers?.village || '',
        d.soil_health_score || 0,
        issues.join('; '),
        d.primary_issue || '',
      ];
    });

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `priority-farmers-${showSegment.toLowerCase()}.csv`;
    a.click();
  };

  const getScoreBadgeClass = (score) => {
    if (score < 40) return 'bg-red-100 text-red-800';
    if (score < 70) return 'bg-amber-100 text-amber-800';
    return 'bg-green-100 text-green-800';
  };

  const getIssues = (test) => {
    const issues = [];
    if (test.organic_carbon !== null && test.organic_carbon < IDEAL_RANGES.organic_carbon.min) issues.push('Low OC');
    if (test.ph !== null && test.ph > IDEAL_RANGES.ph.max) issues.push('High pH');
    if (test.phosphorus !== null && test.phosphorus > IDEAL_RANGES.phosphorus.max) issues.push('High P');
    if (test.potassium !== null && test.potassium < IDEAL_RANGES.potassium.min) issues.push('Low K');
    if ((test.zinc !== null && test.zinc < IDEAL_RANGES.zinc.min) || (test.iron !== null && test.iron < IDEAL_RANGES.iron.min) || (test.boron !== null && test.boron < IDEAL_RANGES.boron.min)) {
      issues.push('Micronutrient');
    }
    return issues;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Priority Farmer List
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Farmers are grouped by advisory priority based on their soil health score. Export the filtered list for field follow-up.
          </p>
        </div>
        <button
          onClick={exportCSV}
          className="flex items-center gap-2 px-4 py-2 bg-[#2E7D32] text-white rounded-lg hover:bg-[#1B5E20] transition-colors text-sm"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        {['High', 'Moderate', 'Low'].map(segment => {
          const isActive = showSegment === segment;
          const segmentClasses = {
            High: isActive ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
            Moderate: isActive ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
            Low: isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
          };

          return (
            <button
              key={segment}
              onClick={() => onSegmentChange(segment)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${segmentClasses[segment]}`}
            >
              {segment}
            </button>
          );
        })}
      </div>

      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <div className="inline-block min-w-full align-middle">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 whitespace-nowrap">Farmer Name</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 whitespace-nowrap">Phone</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 whitespace-nowrap">Village</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 whitespace-nowrap">Soil Score</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 whitespace-nowrap">Key Issues</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 whitespace-nowrap">Primary Advisory</th>
              </tr>
            </thead>
            <tbody>
              {data.map((test, idx) => {
                const issues = getIssues(test);
                const score = test.soil_health_score || 0;
                return (
                  <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-900 break-words whitespace-normal max-w-[180px]">{test.farmers?.name || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{test.farmers?.phone_number || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-600 break-words whitespace-normal max-w-[160px]">{test.farmers?.village || '-'}</td>
                    <td className="py-3 px-4 text-sm whitespace-nowrap">
                      <span className={`inline-block px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getScoreBadgeClass(score)}`}>
                        {score}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <div className="flex gap-1 flex-wrap">
                        {issues.map((issue, i) => (
                          <span key={i} className="inline-block px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                            {issue}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{test.primary_issue || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}